/**
 * ScoreGauge Component
 * Circular progress gauge for scores (extracted from ThreeScoreGauges)
 * Used for Trust/Compliance/Maturity scores
 */

import React from 'react';
import { Card, Progress, Tag, Typography } from 'antd';
import { getScoreColor } from '../../hooks/useTrustScore';
import { spacing } from '../../theme/designTokens';

const { Title, Text } = Typography;

/**
 * ScoreGauge Component
 *
 * @param {string} title - Gauge title
 * @param {number} score - Score value (0-100)
 * @param {string} label - Score band label (e.g., "Excellent", "Good")
 * @param {string} color - Custom color (overrides auto color)
 * @param {function} onClick - Click handler
 * @param {string} size - Gauge size ('small' | 'default' | 'large')
 * @param {boolean} showTag - Show score band tag (default: true)
 * @param {string} subtitle - Subtitle text
 * @param {Object} style - Additional styles
 */
function ScoreGauge({
  title,
  score,
  label,
  color,
  onClick,
  size = 'default',
  showTag = true,
  subtitle = 'Click for details',
  style = {},
}) {
  // Determine gauge width based on size
  const width = size === 'large' ? 150 : size === 'small' ? 80 : 120;
  const strokeWidth = size === 'large' ? 10 : size === 'small' ? 6 : 8;

  // Auto-calculate color if not provided
  const scoreColor = color || getScoreColor(score);

  // Auto-calculate label if not provided
  const scoreLabel = label || (
    score >= 80 ? 'Excellent' :
    score >= 60 ? 'Good' :
    'Needs Attention'
  );

  return (
    <Card
      hoverable={!!onClick}
      onClick={onClick}
      style={{
        textAlign: 'center',
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {title && <Title level={size === 'small' ? 5 : 4}>{title}</Title>}

      <Progress
        type="circle"
        percent={score}
        format={(percent) => `${percent.toFixed(1)}%`}
        strokeColor={scoreColor}
        strokeWidth={strokeWidth}
        width={width}
      />

      {showTag && (
        <div style={{ marginTop: spacing.md }}>
          <Tag color={scoreColor} style={{ fontSize: size === 'small' ? 12 : 14 }}>
            {scoreLabel}
          </Tag>
        </div>
      )}

      {subtitle && onClick && (
        <Text
          type="secondary"
          style={{
            fontSize: size === 'small' ? 11 : 12,
            display: 'block',
            marginTop: spacing.sm,
          }}
        >
          {subtitle}
        </Text>
      )}
    </Card>
  );
}

export default ScoreGauge;
